import type { FC, ReactNode } from 'react'

import type { LucideIcon } from 'lucide-react'

import { Eye, HandHeart, ShieldCheck, Waves } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import Reveal from '@/components/motion/reveal'
import PixelText from '@/components/motion/pixel-text'
import PageHero from '@/components/generals/page-hero'
import FinalCta from '@/components/landing/final-cta'

interface Principle {
    icon: LucideIcon
    title: string
    body: string
}

const principles: Principle[] = [
    {
        icon: ShieldCheck,
        title: 'Earn trust before access',
        body: 'Forge starts read-only and asks for more only when it has shown it can be trusted with it. Every permission is narrow and every action is recorded.'
    },
    {
        icon: Eye,
        title: 'Show the work',
        body: 'A finding without evidence is a guess. Every conclusion Forge reaches links to the traces, metrics and changes behind it.'
    },
    {
        icon: HandHeart,
        title: 'People stay in charge',
        body: 'Forge proposes and your engineers decide. It remembers the calls your team makes and never quietly overrides them.'
    },
    {
        icon: Waves,
        title: 'Quiet is the goal',
        body: 'We measure success in pages that never had to happen, not in dashboards. A good week with Forge is a week nobody notices.'
    }
]

const numbers = [
    { value: '2023', label: 'Founded by former on-call engineers' },
    { value: '46', label: 'People across Toronto and London' },
    { value: '180+', label: 'Engineering teams running Forge' },
    { value: '4.1M', label: 'Alerts handled without a page last month' }
]

const leaders = [
    { initials: 'NC', name: 'Nadia Chen', role: 'Co-founder and CEO', bio: 'Ran site reliability for a large streaming platform and carried the pager for nine years.', tint: '#dfe5d6' },
    { initials: 'OB', name: 'Owen Brightwater', role: 'Co-founder and CTO', bio: 'Built the deploy and observability tooling behind thousands of services at a cloud provider.', tint: '#e6dcef' },
    { initials: 'LS', name: 'Leila Sato', role: 'Head of Research', bio: 'Studies how agents reason over noisy telemetry and how to make that reasoning checkable.', tint: '#d6e4f0' },
    { initials: 'JM', name: 'Jonah Mbeki', role: 'VP of Engineering', bio: 'Leads the teams building the System Map, triage and the fix pipeline.', tint: '#fbe8cf' },
    { initials: 'AR', name: 'Ana Ruiz', role: 'Head of Security', bio: 'Owns Forge’s access model, compliance programs and the audit trail behind every action.', tint: '#f0dcd6' },
    { initials: 'TW', name: 'Tom Whitfield', role: 'Head of Customer Engineering', bio: 'Sits beside every new customer during their pilot and stays through the first year.', tint: '#e2e0d6' }
]

const offices = [
    { city: 'Toronto', detail: 'Headquarters, product and research', address: 'Spadina Avenue, Toronto, Ontario' },
    { city: 'London', detail: 'Customer engineering for Europe', address: 'Clerkenwell Road, London' }
]

const AboutPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='About us'
                description='Forge is built by engineers who carried the pager. Meet the team, the principles behind the product and where we work.'
            />

            <PageHero
                eyebrow='Company'
                title='We build the engineer we always wanted on call.'
                description='Forge started with a simple frustration: the people who keep production running spend their nights on problems a careful machine could have caught.'
            />

            <section aria-labelledby='story-title' data-header-theme='light' className='border-b border-dashed border-line bg-paper'>
                <div className='container-page grid gap-10 py-24 sm:py-32 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:gap-16'>
                    <PixelText
                        id='story-title'
                        as='h2'
                        text='Why Forge exists.'
                        className='font-heading text-[clamp(2.2rem,4.4vw,3.4rem)] font-normal leading-[1.06] tracking-[-0.025em] text-ink'
                    />

                    <Reveal delay={100} className='flex max-w-[38rem] flex-col gap-6 text-pretty text-[17px] leading-relaxed text-ink-muted sm:text-[18px]'>
                        <p>
                            Between us we have carried the pager for more than forty years. We have watched good engineers burn out answering alerts that did not matter, and we have watched real problems slip past dashboards nobody had time to read.
                        </p>

                        <p>
                            We started Forge to give every team a colleague who reads everything, forgets nothing and never gets tired: one that maps the system, follows every thread to its cause and hands back a fix that has already been checked.
                        </p>

                        <p className='text-ink'>
                            The goal has never been to replace the people who run production. It is to give them their nights back.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section aria-labelledby='principles-title' data-header-theme='dark' className='bg-night text-night-ink'>
                <div className='container-page flex flex-col gap-12 py-24 sm:gap-16 sm:py-32'>
                    <PixelText
                        id='principles-title'
                        as='h2'
                        text='What we hold ourselves to.'
                        className='max-w-[30rem] font-heading text-[clamp(2.2rem,4.4vw,3.4rem)] font-normal leading-[1.06] tracking-[-0.025em] text-white text-balance'
                    />

                    <ul className='grid gap-3 sm:grid-cols-2'>
                        {principles.map((principle, index) => (
                            <Reveal as='li' key={principle.title} index={index % 2} className='flex'>
                                <div className='night-card flex w-full flex-col gap-5 p-7 sm:p-9'>
                                    <span className='grid size-10 place-items-center rounded-[10px] bg-white/[0.06] text-night-ink'>
                                        <principle.icon className='size-5' strokeWidth={1.6} aria-hidden='true' />
                                    </span>

                                    <PixelText as='h3' text={principle.title} className='font-heading text-[1.6rem] font-normal leading-tight tracking-[-0.015em] text-white' />

                                    <p className='text-pretty text-[15.5px] leading-relaxed text-night-muted'>
                                        {principle.body}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <section aria-label='Forge in numbers' data-header-theme='light' className='border-b border-dashed border-line bg-paper-lifted'>
                <div className='container-page py-16 sm:py-20'>
                    <dl className='grid gap-px overflow-hidden rounded-2xl bg-line sm:grid-cols-2 lg:grid-cols-4'>
                        {numbers.map((item) => (
                            <div key={item.label} className='flex flex-col gap-3 bg-paper-lifted p-7 sm:p-8'>
                                <dt className='order-2 text-pretty text-[15px] leading-snug text-ink-muted'>
                                    {item.label}
                                </dt>

                                <dd className='order-1 font-heading text-[clamp(2.6rem,4.6vw,3.4rem)] leading-none tracking-[-0.035em] text-ink'>
                                    {item.value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </section>

            <section aria-labelledby='team-title' data-header-theme='light' className='border-b border-dashed border-line bg-paper'>
                <div className='container-page flex flex-col gap-12 py-24 sm:gap-14 sm:py-32'>
                    <PixelText
                        id='team-title'
                        as='h2'
                        text='The people behind Forge.'
                        className='font-heading text-[clamp(2.2rem,4.4vw,3.4rem)] font-normal leading-[1.06] tracking-[-0.025em] text-ink'
                    />

                    <ul className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                        {leaders.map((person, index) => (
                            <Reveal as='li' key={person.name} index={index % 3} className='flex'>
                                <article className='card flex w-full flex-col gap-5 p-7'>
                                    <span
                                        aria-hidden='true'
                                        className='grid size-14 place-items-center rounded-full text-[15px] font-semibold text-ink shadow-[inset_0_0_0_1px_rgb(26_22_18/0.08)]'
                                        style={{ backgroundColor: person.tint }}
                                    >
                                        {person.initials}
                                    </span>

                                    <div className='flex flex-col gap-1'>
                                        <h3 className='text-[1.15rem] font-medium tracking-[-0.01em] text-ink'>
                                            {person.name}
                                        </h3>

                                        <p className='text-[14px] text-ink-subtle'>
                                            {person.role}
                                        </p>
                                    </div>

                                    <p className='text-pretty text-[15px] leading-relaxed text-ink-muted'>
                                        {person.bio}
                                    </p>
                                </article>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <section aria-labelledby='offices-title' data-header-theme='light' className='bg-paper'>
                <div className='container-page grid gap-10 py-24 sm:py-32 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] lg:gap-16'>
                    <PixelText
                        id='offices-title'
                        as='h2'
                        text='Where we work.'
                        className='font-heading text-[clamp(2.2rem,4.4vw,3.4rem)] font-normal leading-[1.06] tracking-[-0.025em] text-ink'
                    />

                    <ul className='grid gap-3 sm:grid-cols-2'>
                        {offices.map((office, index) => (
                            <Reveal as='li' key={office.city} index={index} className='flex'>
                                <div className='card flex w-full flex-col gap-3 p-7'>
                                    <span className='font-heading text-[1.9rem] leading-none tracking-[-0.02em] text-ink'>
                                        {office.city}
                                    </span>

                                    <span className='text-[15px] text-ink-muted'>
                                        {office.detail}
                                    </span>

                                    <span className='mt-4 border-t border-dashed border-line pt-4 text-[14px] text-ink-subtle'>
                                        {office.address}
                                    </span>
                                </div>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <FinalCta />
        </Layout>
    )
}

export default AboutPage
